import React from 'react';
import { FormattedMessage } from 'react-intl';
import { VARIANTS } from '../displayVariant';
import { answerHasCase, isCasedCharacter } from '../explanation';

// Level 1 (nothing to remember) and level 0 (nothing to type): a plain text, no marker, no "memorise", and no
// secret-looking tile (a sample code shown big read as the user's own secret).
const PlainSection = ({ headingId, ruleId, noteId }) => (
    <section className="hia-ack-card hia-ack-card--secret" aria-labelledby="hia-ack-h-secret" data-section="secret">
        <h2 id="hia-ack-h-secret" className="hia-ack-eyebrow"><FormattedMessage id="conventionPage.secret.heading" /></h2>
        <div className="hia-ack-secret">
            <div className="hia-ack-secret-text">
                <p className="hia-ack-secret-label"><FormattedMessage id={headingId} /></p>
                <p><FormattedMessage id={ruleId} /></p>
            </div>
        </div>
        {noteId && <p className="hia-ack-note"><FormattedMessage id={noteId} /></p>}
    </section>
);

// "What you look for": the user's real marker or keyword, large, with the instruction to keep it secret.
// Only generic facts of the parameters are written (case sensitivity, answer length), never a family rule. Case
// sentences only appear when case can matter: a letter marker, an answer that may contain letters.
const SecretSection = ({ variant, parameters }) => {
    if (variant === VARIANTS.COPY) {
        return (
            <PlainSection
                headingId="conventionPage.copy.heading"
                ruleId="conventionPage.copy.rule"
                noteId="conventionPage.copy.note"
            />
        );
    }
    if (variant === VARIANTS.NOTHING_TO_TYPE) {
        return <PlainSection headingId="conventionPage.nothing.heading" ruleId="conventionPage.nothing.rule" />;
    }
    if (variant !== VARIANTS.SECRET) return null;
    const isKeyword = !parameters.marker && !!parameters.keyword;
    const secret = isKeyword ? parameters.keyword : parameters.marker;
    return (
        <section className="hia-ack-card hia-ack-card--secret" aria-labelledby="hia-ack-h-secret" data-section="secret">
            <h2 id="hia-ack-h-secret" className="hia-ack-eyebrow"><FormattedMessage id="conventionPage.secret.heading" /></h2>
            <div className="hia-ack-secret">
                <span className="hia-ack-secret-value" data-testid="secret-value">{secret}</span>
                <div className="hia-ack-secret-text">
                    <p className="hia-ack-secret-label">
                        <FormattedMessage id={isKeyword ? 'conventionPage.secret.keyword' : 'conventionPage.secret.marker'} />
                    </p>
                    {!isKeyword && isCasedCharacter(secret) && typeof parameters.markerCaseSensitive === 'boolean' && (
                        <p className="hia-ack-note">
                            <FormattedMessage
                                id={parameters.markerCaseSensitive
                                    ? 'conventionPage.secret.caseSensitive'
                                    : 'conventionPage.secret.caseInsensitive'}
                            />
                        </p>
                    )}
                    {Number.isInteger(parameters.answerLength) && parameters.answerLength > 0 && (
                        <p className="hia-ack-note">
                            <FormattedMessage
                                id={answerHasCase(parameters) ? 'conventionPage.secret.answerLength' : 'conventionPage.secret.answerLengthPlain'}
                                values={{ count: parameters.answerLength }}
                            />
                        </p>
                    )}
                </div>
            </div>
            <p className="hia-ack-keep"><FormattedMessage id="conventionPage.secret.keep" /></p>
        </section>
    );
};

export default SecretSection;
